'use client';

import { useMemoizedFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { MAX_SELECT_ITEMS } from '../_constants';
import type { FileItem } from '../types';
import { useSelectionBox } from './use-selection-box';

export interface UseFileSelectionOptions {
  files: FileItem[];
  currentFolderId?: string; // 用于判断何时需要清空选择的条件
  disabledSelectionBox?: boolean;
}

export function useFileSelection({ files, currentFolderId, disabledSelectionBox = false }: UseFileSelectionOptions) {
  const t = useTranslations();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const lastClickedIndexRef = useRef<number>(-1);

  const onMaxSelectExceeded = () => {
    toast({
      message: t('creations.warning.maxSelectItems', { count: MAX_SELECT_ITEMS }),
      type: 'error',
    });
  };

  // 清空选择
  const clearSelection = useMemoizedFn(() => {
    setSelectedItems([]);
    lastClickedIndexRef.current = -1;
  });

  // 当文件夹变化时清空选择
  useEffect(() => {
    clearSelection();
  }, [currentFolderId]);

  // ESC 键清空选择
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearSelection();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [clearSelection]);

  // 处理拉框选择
  const handleSelectionChange = (selectedIds: string[]) => {
    // 规范化选中列表（考虑上限），并据此更新锚点
    const normalizedIds = selectedIds.length > MAX_SELECT_ITEMS ? selectedIds.slice(0, MAX_SELECT_ITEMS) : selectedIds;

    if (selectedIds.length > MAX_SELECT_ITEMS) {
      onMaxSelectExceeded();
    }

    setSelectedItems(normalizedIds);

    // 同步更新锚点为当前选中范围中索引最大的项，便于后续 Shift 扩选
    if (normalizedIds.length > 0) {
      const selectedIndexes = normalizedIds.map((id) => files.findIndex((f) => f.id === id)).filter((idx) => idx >= 0);
      if (selectedIndexes.length > 0) {
        const maxIndex = Math.max(...selectedIndexes);
        lastClickedIndexRef.current = maxIndex;
      }
    }
  };

  // 集成拉框选择功能
  const { selectionBox, isSelecting, containerRef } = useSelectionBox({
    onSelectionChange: handleSelectionChange,
    disabled: disabledSelectionBox,
  });

  // 单个文件选择/取消选择
  const handleSelectItem = useMemoizedFn((itemId: string) => {
    setSelectedItems((prev) => {
      const isCurrentlySelected = prev.includes(itemId);

      // 如果是取消选择，直接允许
      if (isCurrentlySelected) {
        return prev.filter((id) => id !== itemId);
      }

      // 如果是新增选择，检查是否超出限制
      if (prev.length >= MAX_SELECT_ITEMS) {
        onMaxSelectExceeded();
        return prev; // 不做任何改变
      }

      return [...prev, itemId];
    });

    // 更新锚点索引，便于后续 Shift 范围选择
    const index = files.findIndex((file) => file.id === itemId);
    if (
      index !== -1 &&
      lastClickedIndexRef &&
      typeof lastClickedIndexRef === 'object' &&
      lastClickedIndexRef !== null &&
      'current' in lastClickedIndexRef
    ) {
      lastClickedIndexRef.current = index;
    }
  });

  // 全选/取消全选
  const handleSelectAll = useMemoizedFn((checked?: boolean | 'indeterminate') => {
    if (checked === true && files) {
      // 如果当前已选中数量达到最大限制
      if (selectedItems.length >= MAX_SELECT_ITEMS) {
        toast({
          message: t('creations.warning.maxSelectItemsUnSelect', { count: MAX_SELECT_ITEMS }),
          type: 'warning',
        });
        clearSelection();
        return;
      }

      // 正常全选逻辑
      const maxSelection = Math.min(files.length, MAX_SELECT_ITEMS);
      setSelectedItems(files.slice(0, maxSelection).map((file) => file.id));
    } else {
      clearSelection();
    }
  });

  // 处理点击选择逻辑（支持Ctrl/Cmd多选和Shift范围选择）
  const handleItemClick = (itemId: string, event?: React.MouseEvent) => {
    if (!event) return;

    const itemIndex = files.findIndex((file) => file.id === itemId);
    const item = files[itemIndex];

    if (!item) return;

    // Ctrl/Cmd 键多选
    if (event.ctrlKey || event.metaKey) {
      handleSelectItem(itemId);
      lastClickedIndexRef.current = itemIndex;
      return { type: 'select' as const, item };
    }
    // Shift 键范围选择
    if (event.shiftKey && lastClickedIndexRef.current !== -1) {
      const startIndex = Math.min(lastClickedIndexRef.current, itemIndex);
      const endIndex = Math.max(lastClickedIndexRef.current, itemIndex);
      const rangeIds = files.slice(startIndex, endIndex + 1).map((file) => file.id);

      // 如果范围选择超出限制，截取前 maxSelectItems 个并触发回调
      if (rangeIds.length > MAX_SELECT_ITEMS) {
        onMaxSelectExceeded();
        setSelectedItems(rangeIds.slice(0, MAX_SELECT_ITEMS));
      } else {
        setSelectedItems(rangeIds);
      }
      return { type: 'select' as const, item };
    }
    // 第一次按住 Shift 点击时，仅建立锚点并选中该项，不进行打开
    if (event.shiftKey && lastClickedIndexRef.current === -1) {
      setSelectedItems([itemId]);
      lastClickedIndexRef.current = itemIndex;
      return { type: 'select' as const, item };
    }
    // 正常单击
    lastClickedIndexRef.current = itemIndex;
    return { type: 'open' as const, item };
  };

  return {
    // 状态
    selectedItems,
    containerRef,
    selectionBox,
    isSelecting,
    // 方法
    handleItemClick,
    handleSelectItem,
    handleSelectAll,
    clearSelection,
    setSelectedItems,
  };
}
